import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import { getLegalContent } from '../api/legal';
import { useT } from '../hooks/useT';
import { LoadingSkeleton } from './LoadingSkeleton';
import { LanguageToggle } from './LanguageToggle';
import type { LegalContent } from '../types/dtos';

type Props = { slug: string };

/**
 * Public, branded legal page rendered at /impressum, /datenschutz, /terms, /data-deletion.
 * Fetches sanitized HTML from the anonymous GET /v1/legal/{slug} endpoint. No booking token.
 */
export function LegalPage({ slug }: Props) {
  const { t, locale } = useT();
  const q = useQuery<LegalContent, ApiError>({
    queryKey: ['legal', slug, locale],
    queryFn: () => getLegalContent(slug, locale),
    retry: (failureCount, err) =>
      err instanceof ApiError && err.status >= 400 && err.status < 500 ? false : failureCount < 2,
    staleTime: 300_000,
  });

  if (q.isLoading) return <LoadingSkeleton />;

  return (
    <main className="mx-auto max-w-2xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="text-sm text-slate-500 hover:text-brand hover:underline"
        >
          ← {t('legal.back')}
        </button>
        <LanguageToggle compact />
      </div>

      {q.isError ? (
        <p className="text-slate-600">{t('legal.not_available')}</p>
      ) : (
        <>
          <h1 className="mb-6 text-2xl font-semibold text-slate-900">{q.data!.title}</h1>
          <div
            className="space-y-3 text-slate-700"
            dangerouslySetInnerHTML={{ __html: q.data!.bodyHtml }}
          />
        </>
      )}
    </main>
  );
}
